import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import GetBatchNotes from "../../actions/notes/GetBatchNotes";
import UploadBatchNote from "../../actions/notes/UploadBatchNote";
import DeleteBatchNote from "../../actions/notes/DeleteBatchNote";

/* Notes tab for /batch-management/:course_url/:batch_id
 * Admin uploads PDF / PPT notes for this specific batch. Students read them
 * from /batch-notes/:course_url/:batch_id on the main site. */
const BatchNotesManager = ({ course_url, batch_id }) => {
  const { user } = useContext(AuthContext);
  const session = user ? user.SessionId : null;

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmNote, setConfirmNote] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadNotes = async () => {
    if (!session) return;
    setLoading(true);
    try {
      const res = await GetBatchNotes({
        payload: { session, course_url, batch_id },
      });
      setNotes(res && res.status === "success" ? res.data || [] : []);
    } catch (e) {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course_url, batch_id, session]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setFile(null);
    setProgress(0);
    const input = document.getElementById("note-file-input");
    if (input) input.value = "";
  };

  const onUpload = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!title.trim()) {
      setMessage({ type: "error", text: "Please enter a title." });
      return;
    }
    if (!file) {
      setMessage({ type: "error", text: "Please choose a PDF or PPT file." });
      return;
    }
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (ext !== "pdf") {
      setMessage({
        type: "error",
        text:
          "Only PDF files are allowed. In PowerPoint use File > Save as > PDF, " +
          "then upload that — PDFs are the only format students cannot download.",
      });
      return;
    }

    setBusy(true);
    try {
      const res = await UploadBatchNote({
        payload: { session, course_url, batch_id, title, description, file },
        onProgress: setProgress,
      });
      if (res && res.status === "success") {
        setMessage({ type: "success", text: "Note uploaded." });
        resetForm();
        loadNotes();
      } else {
        setMessage({
          type: "error",
          text: (res && res.message) || "Upload failed. Please try again.",
        });
        setProgress(0);
      }
    } catch (err) {
      setMessage({ type: "error", text: "Upload failed. Please try again." });
      setProgress(0);
    } finally {
      setBusy(false);
    }
  };

  /* Deletion is confirmed in the modal below instead of a browser alert. */
  const confirmDelete = async () => {
    const note = confirmNote;
    if (!note) return;
    setDeleting(true);
    try {
      const res = await DeleteBatchNote({ payload: { session, id: note.id } });
      if (res && res.status === "success") {
        setNotes((prev) => prev.filter((n) => n.id !== note.id));
      } else {
        setMessage({ type: "error", text: (res && res.message) || "Delete failed." });
      }
    } catch (e) {
      setMessage({ type: "error", text: "Delete failed." });
    } finally {
      setDeleting(false);
      setConfirmNote(null);
    }
  };

  const prettySize = (kb) => {
    if (!kb) return "";
    return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;
  };

  return (
    <div className="batch-notes">
      <style>{`
        .batch-notes .note-card {
          background: #fff;
          border: 1px solid #e9edf2;
          border-radius: 12px;
          padding: 16px;
        }
        .batch-notes .note-row {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 16px;
          border: 1px solid #e9edf2;
          border-radius: 12px;
          background: #fff;
          margin-bottom: 10px;
        }
        .batch-notes .note-type {
          width: 44px; height: 44px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700; letter-spacing: .02em;
          flex-shrink: 0;
        }
        .batch-notes .type-pdf { background: rgba(229,57,53,.10); color: #c62828; }
        .batch-notes .type-ppt { background: rgba(245,124,0,.12); color: #e65100; }
        .batch-notes .note-title { font-weight: 600; color: #16202c; margin: 0; }
        .batch-notes .note-desc { font-size: 13px; color: #6b7280; margin: 2px 0 0; }
        .batch-notes .note-meta { font-size: 12px; color: #9aa1ab; margin-top: 2px; }
        .batch-notes .form-label-sm { font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 4px; }

        /* delete confirmation modal */
        .note-modal-backdrop {
          position: fixed; top: 0; right: 0; bottom: 0; left: 0;
          z-index: 20000;
          background: rgba(17, 24, 39, .55);
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
        }
        .note-modal {
          background: #fff; border-radius: 14px; padding: 22px;
          width: 100%; max-width: 420px;
          box-shadow: 0 20px 50px rgba(0, 0, 0, .25);
        }
        .note-modal-title { margin: 0 0 8px; font-weight: 700; color: #16202c; }
        .note-modal-text {
          margin: 0 0 18px; font-size: 14px; color: #4b5563; line-height: 1.6;
        }
        .note-modal-actions {
          display: flex; justify-content: flex-end; gap: 10px;
        }
      `}</style>

      <div className="row g-4">
        {/* ---------- upload form ---------- */}
        <div className="col-12 col-lg-5">
          <div className="note-card">
            <h5 className="mb-1">Add a note</h5>
            <p className="text-muted" style={{ fontSize: 13 }}>
              Upload a PDF for this batch. Students can read it in their course
              dashboard but cannot download it. Export PowerPoint decks as PDF
              first (File &gt; Save as &gt; PDF).
            </p>

            <form onSubmit={onUpload} className="mt-3">
              <div className="mb-3">
                <div className="form-label-sm">Title</div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Introduction to DFT"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <div className="form-label-sm">Description (optional)</div>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Short summary of what this note covers"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <div className="form-label-sm">File (PDF only)</div>
                <input
                  id="note-file-input"
                  type="file"
                  className="form-control"
                  accept=".pdf,application/pdf"
                  onChange={(e) => setFile(e.target.files[0])}
                />
                {file ? (
                  <div className="note-meta mt-1">
                    {file.name} — {prettySize(Math.round(file.size / 1024))}
                  </div>
                ) : null}
              </div>

              {busy && progress > 0 ? (
                <div className="progress mb-3" style={{ height: 6 }}>
                  <div
                    className="progress-bar"
                    style={{ width: `${progress}%`, background: "#f4611e" }}
                  />
                </div>
              ) : null}

              {message ? (
                <div
                  className={`alert ${
                    message.type === "success" ? "alert-success" : "alert-danger"
                  } py-2`}
                  style={{ fontSize: 13 }}
                >
                  {message.text}
                </div>
              ) : null}

              <button className="btn btn-primary w-100" type="submit" disabled={busy}>
                {busy ? `Uploading ${progress}%...` : "Upload note"}
              </button>
            </form>
          </div>
        </div>

        {/* ---------- list ---------- */}
        <div className="col-12 col-lg-7">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="mb-0" style={{ color: "#fff" }}>
              Batch notes
            </h5>
            <button className="btn btn-sm btn-outline-secondary" onClick={loadNotes}>
              Refresh
            </button>
          </div>

          {loading ? (
            <p className="text-muted">Loading notes...</p>
          ) : notes.length === 0 ? (
            <div className="note-card text-center py-5">
              <p className="mb-1 fw-semibold">No notes yet</p>
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                Upload the first PDF or PPT for this batch.
              </p>
            </div>
          ) : (
            notes.map((note) => (
              <div className="note-row" key={note.id}>
                <div
                  className={`note-type ${
                    note.file_type === "pdf" ? "type-pdf" : "type-ppt"
                  }`}
                >
                  {note.file_type === "pdf" ? "PDF" : "PPT"}
                </div>

                <div className="flex-grow-1 overflow-hidden">
                  <p className="note-title text-truncate">{note.title}</p>
                  {note.description ? (
                    <p className="note-desc text-truncate">{note.description}</p>
                  ) : null}
                  <div className="note-meta">
                    {prettySize(note.size_kb)}
                    {note.uploaded_on ? ` • ${note.uploaded_on}` : ""}
                  </div>
                </div>

                <a
                  className="btn btn-sm btn-outline-primary"
                  href={
                    note.file_type === "pdf"
                      ? `${note.view_url}#toolbar=0&navpanes=0`
                      : note.office_url
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View
                </a>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setConfirmNote(note)}
                >
                  Delete
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ---------- delete confirmation ---------- */}
      {confirmNote ? (
        <div
          className="note-modal-backdrop"
          onClick={() => {
            if (!deleting) setConfirmNote(null);
          }}
        >
          <div
            className="note-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h5 className="note-modal-title">Delete this note?</h5>
            <p className="note-modal-text">
              Are you sure you want to delete{" "}
              <strong>"{confirmNote.title}"</strong>? It will be removed for
              every student in this batch and cannot be undone.
            </p>
            <div className="note-modal-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setConfirmNote(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BatchNotesManager;
